import { useMemo, useState } from 'react';
import {
  ArrowRightLeft, MapPin, MapPinOff, MoreHorizontal, Pencil, Plus, Power, Star, Store as StoreIcon, Trash2,
} from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import {
  useStoreBranchStockQuery, useStoreBranchesQuery, type StoreBranch,
} from '@/client/api/stores/parity.query';
import {
  useClearStoreBranchVenue, useCreateStoreBranch, useDeleteStoreBranch, useMoveStoreOrderBranch,
  useSetStoreBranchVenue, useSetStorePrimaryBranch, useTransferStoreStock, useUpdateStoreBranch,
} from '@/client/api/stores/parity.mutation';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { CardSkeleton, ListSkeleton, Pill, SectionCard } from '../shared';
import { IdLookup } from '../../IdLookup';
import { ReasonDialog } from '../ReasonDialog';
import { FieldRow } from './controls';

/**
 * A store's branches (ff-api business rev 0510): one storefront, several shops.
 * The main branch is the store's own address and venue; units not assigned to
 * another branch are the main branch's. Every write calls the same service the
 * seller's Store Manager uses, needs a reason, and notifies the seller.
 */

type Action = 'rename' | 'link' | 'unlink' | 'pickup' | 'active' | 'primary' | 'remove';

const ACTION_COPY: Record<Action, { title: (b: StoreBranch) => string; description: (b: StoreBranch) => string; confirm: string; destructive?: boolean }> = {
  rename: { title: (b) => `Rename ${b.name ?? 'branch'}`, description: () => 'Buyers see this name at checkout pickup.', confirm: 'Save' },
  link: { title: (b) => `Link ${b.name ?? 'branch'} to a place`, description: (b) => (b.is_primary ? 'For the main branch this is the store’s venue.' : 'The branch takes its area and map pin from the place.'), confirm: 'Link' },
  unlink: { title: (b) => `Unlink ${b.name ?? 'branch'}`, description: () => 'The place stays on the map; it is released if nothing else holds it.', confirm: 'Unlink', destructive: true },
  pickup: { title: (b) => `${b.pickup_enabled ? 'Stop' : 'Offer'} pickup at ${b.name ?? 'branch'}`, description: (b) => (b.pickup_enabled ? 'Buyers can no longer choose this branch for collection.' : 'Buyers can choose this branch for collection.'), confirm: 'Confirm' },
  active: { title: (b) => `${b.active ? 'Switch off' : 'Switch on'} ${b.name ?? 'branch'}`, description: (b) => (b.active ? 'It stops fulfilling new orders; its stock stays where it is.' : 'It can fulfil orders again.'), confirm: 'Confirm', destructive: true },
  primary: { title: (b) => `Make ${b.name ?? 'branch'} the main branch`, description: () => 'Its address and place become the store’s. The current main branch keeps its stock as an ordinary branch; no counts change.', confirm: 'Make main' },
  remove: { title: (b) => `Remove ${b.name ?? 'branch'}`, description: () => 'Refused while the branch holds stock — move it first.', confirm: 'Remove', destructive: true },
};

const MENU: { action: Action; label: (b: StoreBranch) => string; icon: React.ElementType; show: (b: StoreBranch) => boolean; destructive?: boolean }[] = [
  { action: 'rename', label: () => 'Rename', icon: Pencil, show: () => true },
  { action: 'link', label: (b) => (b.eid ? 'Change place' : 'Link a place'), icon: MapPin, show: () => true },
  { action: 'unlink', label: () => 'Unlink place', icon: MapPinOff, show: (b) => !!b.eid },
  { action: 'pickup', label: (b) => (b.pickup_enabled ? 'Stop pickup here' : 'Offer pickup here'), icon: StoreIcon, show: (b) => !b.is_primary },
  { action: 'active', label: (b) => (b.active ? 'Switch off' : 'Switch on'), icon: Power, show: (b) => !b.is_primary },
  { action: 'primary', label: () => 'Make main branch', icon: Star, show: (b) => !b.is_primary && b.active },
  { action: 'remove', label: () => 'Remove', icon: Trash2, show: (b) => !b.is_primary, destructive: true },
];

const addressText = (b: StoreBranch): string => {
  const a = b.pickup_address ?? {};
  const text = (a.address ?? a.address_text ?? a.gpgpsCode) as string | undefined;
  if (text) return text;
  if (b.latitude != null && b.longitude != null) return `${b.latitude.toFixed(5)}, ${b.longitude.toFixed(5)}`;
  return 'No address';
};

const BranchRow: React.FC<{ store: AdminStore; branch: StoreBranch; canEdit: boolean }> = ({ store, branch: b, canEdit }) => {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<Action | null>(null);
  const [name, setName] = useState(b.name ?? '');
  const [eid, setEid] = useState('');
  const [allowClaimed, setAllowClaimed] = useState(false);
  const update = useUpdateStoreBranch();
  const remove = useDeleteStoreBranch();
  const primary = useSetStorePrimaryBranch();
  const link = useSetStoreBranchVenue();
  const unlink = useClearStoreBranchVenue();
  const busy = update.isPending || remove.isPending || primary.isPending || link.isPending || unlink.isPending;

  const run = (reason: string) => {
    const base = { storeid: store.storeid, branch_id: b.branch_id, reason };
    const done = { onSuccess: () => setAction(null) };
    switch (action) {
      case 'rename': return update.mutate({ ...base, name: name.trim() }, done);
      case 'pickup': return update.mutate({ ...base, pickup_enabled: !b.pickup_enabled }, done);
      case 'active': return update.mutate({ ...base, active: !b.active }, done);
      case 'primary': return primary.mutate(base, done);
      case 'remove': return remove.mutate(base, done);
      case 'link': return link.mutate({ ...base, eid: Number(eid), allow_claimed: allowClaimed }, done);
      case 'unlink': return unlink.mutate(base, done);
      default: return undefined;
    }
  };

  return (
    <div className="py-3">
      <div className="flex items-start gap-3">
        <button type="button" onClick={() => setOpen((o) => !o)} className="min-w-0 flex-1 text-left" aria-expanded={open}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{b.name ?? 'Unnamed branch'}</span>
            {b.is_primary && <Pill tone="bg-blue-100 text-blue-800">Main</Pill>}
            {!b.active && <Pill tone="bg-gray-100 text-gray-700">Off</Pill>}
            {b.pickup_enabled && b.active && <Pill tone="bg-green-100 text-green-800">Pickup</Pill>}
          </div>
          <p className="mt-0.5 truncate text-xs text-gray-500">
            {addressText(b)}
            {b.venue ? ` · Place: ${b.venue.name ?? `#${b.venue.eid}`}` : ''}
          </p>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" disabled={!canEdit} aria-label={`Actions for ${b.name ?? 'branch'}`}
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {MENU.filter((m) => m.show(b)).map((m) => (
              <div key={m.action}>
                {m.destructive && <DropdownMenuSeparator />}
                <DropdownMenuItem onSelect={() => setAction(m.action)} className={m.destructive ? 'text-red-600' : undefined}>
                  <m.icon className="mr-2 h-4 w-4" />
                  {m.label(b)}
                </DropdownMenuItem>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {open && (
        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-gray-50 p-3 text-xs text-gray-600">
          <dt>Branch id</dt><dd>{b.branch_id}</dd>
          <dt>Place</dt><dd>{b.eid ? `#${b.eid}` : 'Not linked'}</dd>
          <dt>Area (lid)</dt><dd>{b.lid ?? 'Not set'}</dd>
          <dt>Map pin</dt><dd>{b.latitude != null ? `${b.latitude}, ${b.longitude}` : 'Not set'}</dd>
          <dt>Delivers to</dt>
          <dd>{b.delivery_locations.length ? `${b.delivery_locations.length} area(s): ${b.delivery_locations.join(', ')}` : 'Same as the store'}</dd>
        </dl>
      )}
      {action && (
        <ReasonDialog
          open
          title={ACTION_COPY[action].title(b)}
          description={ACTION_COPY[action].description(b)}
          confirmLabel={ACTION_COPY[action].confirm}
          destructive={ACTION_COPY[action].destructive}
          reasonRequired
          loading={busy}
          onConfirm={(reason) => {
            if (action === 'rename' && !name.trim()) return;
            if (action === 'link' && !eid) return;
            run(reason);
          }}
          onClose={() => setAction(null)}
        >
          {action === 'rename' && (
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Branch name" maxLength={150} />
          )}
          {action === 'link' && (
            <div className="space-y-2">
              <IdLookup kind="listing" value={eid} onChange={setEid} placeholder="Search place by name, or type an eid" />
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input type="checkbox" checked={allowClaimed} onChange={(e) => setAllowClaimed(e.target.checked)} />
                Move even if another owner’s store holds it
              </label>
            </div>
          )}
        </ReasonDialog>
      )}
    </div>
  );
};

const AddBranch: React.FC<{ store: AdminStore; onClose: () => void }> = ({ store, onClose }) => {
  const [name, setName] = useState('');
  const [eid, setEid] = useState('');
  const create = useCreateStoreBranch();
  return (
    <ReasonDialog
      open
      title="Add a branch"
      description="A shop this storefront sells from. Link the place it is so its area and map pin come with it."
      confirmLabel="Add"
      reasonRequired
      loading={create.isPending}
      onConfirm={(reason) => {
        if (!name.trim()) return;
        create.mutate({ storeid: store.storeid, name: name.trim(), eid: eid ? Number(eid) : null, reason },
          { onSuccess: onClose });
      }}
      onClose={onClose}
    >
      <div className="space-y-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, e.g. Adum branch" maxLength={150} />
        <IdLookup kind="listing" value={eid} onChange={setEid} placeholder="Place listing (optional)" />
      </div>
    </ReasonDialog>
  );
};

const BranchSelect: React.FC<{ branches: StoreBranch[]; value: string; onChange: (v: string) => void; label: string }> = ({ branches, value, onChange, label }) => (
  <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}
    className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm">
    <option value="">{label}</option>
    {branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.name ?? `Branch ${b.branch_id}`}{b.is_primary ? ' (main)' : ''}</option>)}
  </select>
);

/** Where one listing's units are, and moving them between branches. */
const StockCard: React.FC<{ store: AdminStore; branches: StoreBranch[] }> = ({ store, branches }) => {
  const [eid, setEid] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [qty, setQty] = useState('1');
  const [confirm, setConfirm] = useState(false);
  const eidNum = eid ? Number(eid) : null;
  const { data, isLoading, isFetching } = useStoreBranchStockQuery(store.storeid, eidNum);
  const transfer = useTransferStoreStock();
  const canMove = useGate(PERMISSIONS.store.inventory).allowed;
  const rows = useMemo(() => (eidNum ? data?.stock?.[String(eidNum)] ?? [] : []), [data, eidNum]);
  const valid = !!eidNum && !!from && !!to && from !== to && Number(qty) >= 1;

  return (
    <SectionCard title="Stock by branch" description="Pick a listing to see where its units are. Moving units never changes the listing’s total.">
      <IdLookup kind="listing" value={eid} onChange={setEid} className="w-80" placeholder="Search a listing in this store" />
      {eidNum && (isLoading || isFetching) && <p className="mt-3 text-xs text-gray-500">Loading stock for listing #{eidNum}…</p>}
      {eidNum && !isLoading && !rows.length && <p className="mt-3 text-xs text-gray-500">This listing does not track stock in this store.</p>}
      {rows.length > 0 && (
        <table className="mt-3 w-full text-sm">
          <thead><tr className="text-left text-xs text-gray-500"><th className="py-1">Branch</th><th>On hand</th><th>Reserved</th><th>Free</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.branch_id} className="border-t border-gray-100">
                <td className="py-1.5">{r.name}{r.is_primary ? ' (main)' : ''}{!r.active ? ' · off' : ''}</td>
                <td>{r.quantity}</td><td>{r.reserved}</td><td className="font-medium">{r.available}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rows.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <BranchSelect branches={branches} value={from} onChange={setFrom} label="From" />
          <BranchSelect branches={branches} value={to} onChange={setTo} label="To" />
          <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className="h-9 w-20" aria-label="Units" />
          <PermissionButton code={PERMISSIONS.store.inventory} size="sm" disabled={!valid || !canMove} onClick={() => setConfirm(true)}>
            <ArrowRightLeft className="mr-1.5 h-4 w-4" />Move units
          </PermissionButton>
        </div>
      )}
      {confirm && (
        <ReasonDialog
          open
          title={`Move ${qty} unit(s) of listing #${eidNum}`}
          description="Only free units move; units reserved for open orders stay put."
          confirmLabel="Move"
          reasonRequired
          loading={transfer.isPending}
          onConfirm={(reason) =>
            transfer.mutate(
              { storeid: store.storeid, eid: eidNum as number, quantity: Number(qty), from_branch_id: Number(from), to_branch_id: Number(to), reason },
              { onSuccess: () => setConfirm(false) },
            )
          }
          onClose={() => setConfirm(false)}
        />
      )}
    </SectionCard>
  );
};

/** Fulfil an order from a different branch (not for buyer pickup or shipped orders). */
const OrderBranchCard: React.FC<{ store: AdminStore; branches: StoreBranch[] }> = ({ store, branches }) => {
  const [orderid, setOrderid] = useState('');
  const [to, setTo] = useState('');
  const [confirm, setConfirm] = useState(false);
  const move = useMoveStoreOrderBranch();
  return (
    <SectionCard title="Move an order to another branch" description="Its units are released at the old branch and reserved at the new one. Not for orders the buyer is collecting, or that have shipped.">
      <FieldRow label="Order">
        <div className="flex flex-wrap items-center gap-2">
          <Input value={orderid} onChange={(e) => setOrderid(e.target.value.replace(/\D/g, ''))} placeholder="Order id" className="h-9 w-32" />
          <BranchSelect branches={branches.filter((b) => b.active)} value={to} onChange={setTo} label="Fulfil from" />
          <PermissionButton code={PERMISSIONS.store.orders} size="sm" disabled={!orderid || !to} onClick={() => setConfirm(true)}>
            Move order
          </PermissionButton>
        </div>
      </FieldRow>
      {confirm && (
        <ReasonDialog
          open
          title={`Fulfil order #${orderid} from another branch`}
          description="Refused if that branch cannot fill every line."
          confirmLabel="Move"
          reasonRequired
          loading={move.isPending}
          onConfirm={(reason) =>
            move.mutate({ storeid: store.storeid, orderid: Number(orderid), branch_id: Number(to), reason },
              { onSuccess: () => { setConfirm(false); setOrderid(''); } })
          }
          onClose={() => setConfirm(false)}
        />
      )}
    </SectionCard>
  );
};

export const BranchesSection: React.FC<{ store: AdminStore }> = ({ store }) => {
  const { data, isLoading } = useStoreBranchesQuery(store.storeid);
  const [adding, setAdding] = useState(false);
  const canEdit = useGate(PERMISSIONS.store.profile).allowed;
  const branches = data?.branches ?? [];

  if (isLoading)
    return (
      <CardSkeleton description>
        <p className="mb-2 text-xs text-gray-500">Loading branches…</p>
        <ListSkeleton rows={2} />
      </CardSkeleton>
    );

  return (
    <div className="space-y-4">
      <SectionCard
        title="Branches"
        description="Shops this storefront sells from. The main branch is the store’s own address and venue; stock not assigned to another branch is the main branch’s."
        actions={
          <PermissionButton code={PERMISSIONS.store.profile} size="sm" onClick={() => setAdding(true)}>
            <Plus className="mr-1.5 h-4 w-4" />Add branch
          </PermissionButton>
        }
      >
        <div className="divide-y divide-gray-100">
          {branches.map((b) => <BranchRow key={`${b.branch_id}-${b.name}-${b.eid}-${b.is_primary}`} store={store} branch={b} canEdit={canEdit} />)}
        </div>
        {adding && <AddBranch store={store} onClose={() => setAdding(false)} />}
      </SectionCard>
      {branches.length > 1 && <StockCard store={store} branches={branches} />}
      {branches.length > 1 && <OrderBranchCard store={store} branches={branches} />}
    </div>
  );
};
